const post = {
  slug: 'youtube-video-script-structure',
  cluster: 'scripting',
  eyebrow: 'Scripting',
  title: 'A structure for scripts that hold to the end',
  description:
    'Five parts, what each one is for, and the specific place in the middle where most scripts quietly die.',
  keywords: [
    'youtube script structure', 'video script template', 'script outline',
    'how to structure a video', 'video narrative structure', 'script format',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-video-script-structure.webp',
    alt: 'A five-part script skeleton with the mid-section marked as the point of greatest attrition.',
  },
  lede:
    'This is a shape, not a template — templates produce interchangeable videos. What makes it work is that each part has a job, and you can tell when one is not doing it.',
  sections: [
    {
      heading: 'The five parts',
      body: [
        {
          type: 'list',
          ordered: true,
          items: [
            '**The question** — establish what is unresolved. Fifteen to thirty seconds.',
            '**The stakes** — why the answer matters, and to whom. Short.',
            '**The complication** — the reason the obvious answer is wrong. This is the part that makes it a video rather than a fact.',
            '**The resolution** — the actual answer, arriving where you planned it.',
            '**The consequence** — what changed as a result. Not a summary.',
          ],
        },
        {
          type: 'para',
          text: 'Parts three and five are the ones usually missing, and their absence is what produces a video that is accurate and inert.',
        },
      ],
    },
    {
      heading: 'The complication is the video',
      body: [
        {
          type: 'para',
          text: 'Without it you have a question and an answer, which is a fact, not a story. The complication is whatever makes the straightforward answer insufficient.',
        },
        {
          type: 'example',
          bad: 'Q: Why did the company fail? A: They ran out of money.',
          good: 'Q: Why did the company fail? Complication: they had more cash than their competitor eighteen months earlier — and spent it on the thing that was supposed to save them. A: the strategy was right and the timing made it fatal.',
          note: 'The complication is what turns a two-sentence answer into eight minutes that stay interesting.',
        },
        {
          type: 'para',
          text: 'If you cannot find a complication, that is real information: the topic may genuinely be a fact rather than a video. Better to discover that now than at the edit.',
        },
      ],
    },
    {
      heading: 'Where scripts die: the middle',
      body: [
        {
          type: 'para',
          text: 'The most common retention pattern on a well-made video is not a cliff. It is a steady bleed starting around a third of the way in.',
        },
        {
          type: 'para',
          text: 'The cause is almost always the same. The opening was written carefully, the ending was planned, and the middle became evenly-weighted paragraphs of correct information. Nothing is pending. The viewer does not leave because it got worse — they leave because there is no longer anything they are waiting for.',
        },
        {
          type: 'para',
          text: 'Two fixes, and they compose:',
        },
        {
          type: 'list',
          items: [
            '**Keep something unresolved at all times.** When one question closes, open the next in the same breath.',
            '**Land a real moment past the halfway mark.** A scene with a time, a place and a person — not another explanatory paragraph. See [open loops](/blogs/youtube-retention-open-loops).',
          ],
        },
      ],
    },
    {
      heading: 'The re-hook',
      body: [
        {
          type: 'para',
          text: 'Somewhere after the midpoint, the video needs a second reason to keep watching. Not a repeat of the first — a new one.',
        },
        {
          type: 'para',
          text: 'The strongest versions: a consequence nobody saw coming, a second problem created by the first solution, or the detail that reframes what the viewer thought they already understood. That last one is the best of the three, because it retroactively makes the earlier part of the video more interesting.',
        },
      ],
    },
    {
      heading: 'Endings',
      body: [
        {
          type: 'para',
          text: 'The ending is not a summary. Restating what you just said is the most common ending and the weakest — nobody who watched needs it, and it is where people leave before the outro.',
        },
        {
          type: 'para',
          text: 'End on the consequence: what is different now, what it means for the thing the viewer cares about, or the question this answer opens. That last option is also how you get someone to watch a second video.',
        },
        {
          type: 'callout',
          title: 'The test for the whole structure',
          text: 'What changes between the first line and the last? If nothing changes — if the script covers ground point by point — the structure has failed regardless of how well each part is written.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'Does this work for list videos?',
      a: 'Partly. Each item can carry a miniature version of the shape, and the list as a whole still needs an ordering logic that builds rather than an arbitrary sequence. A countdown works because the ordering itself is the open loop.',
    },
    {
      q: 'Should I write the ending first?',
      a: 'Writing it early helps, because knowing where you are landing tells you what the middle needs to set up. Many scripts drift because the ending was discovered rather than chosen.',
    },
  ],
  related: ['youtube-script-writing', 'youtube-retention-open-loops', 'script-pacing-and-shot-length'],
};

export default post;
